import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import privateAxios from "../../utils/axiosPrivate";
import TablePagesTitle from "../../components/table-pages-title/TablePagesTitle";
import AccessPageAction from "../accessmanagment/page-action/AccessPageAction";
import { showToast } from "../../utils/utils";
import {
  commonErrMsg,
  createPricingPlan,
  buttonName,
  alertActions,
  createAccPage,
  fieldValidation
} from "../../locales/en";
import { showToastType } from "../../utils/constants/appConstants";
import { useSelector } from "react-redux";

import { getPicingPlanURL } from "../../utils/constants/apiEndPoints";
import {
  isApplicableField,
  isPercentageField
} from "../../utils/constants/formDataFields";
import { useQuoteStatus } from "../../hooks/useQuoteStatus";
import { PRICING_PLANS } from "../../route/paths";
import { getPricingPlanFormConfig } from "../../utils/constants/pricingPlanFormConfig";
import PricingPlanForm from "./PricingPlanForm";

export default function CreatePricingPlan() {
  const { planGroup } = useSelector((state) => state.pricingPlan);
  const userStatus = useQuoteStatus();
  const initialPricingPlan = {
    pricingPlanName: "",
    flatRate: "",
    annualRate: "",
    extraFlatRate: "",
    extraAnnualRate: "",
    isAutoSwitching: true,
    triggerEvents: [],
    industryCategoryId: null,
    invoiceSyncType: "NA",
    eligibleOrganizationType: "NA",
    applicableCountry: "NA",
    ownershipStatus: "NA",
    isRiskBased: false,
    minApplicableValue: "",
    maxApplicableValue: "",
    primaryPricingPlanId: ""
  };
  const [pricingPlan, setPricingPlan] = useState(initialPricingPlan);
  const [errors, setErrors] = useState({});
  const history = useNavigate();
  const {
    pricingPlanName,
    flatRate,
    annualRate,
    extraFlatRate,
    extraAnnualRate,
    triggerEvents,
    minApplicableValue,
    maxApplicableValue,
    primaryPricingPlanId
  } = pricingPlan;

  const onInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    let newValue = type === "checkbox" ? checked : value;

    if (name === "isAutoSwitching") {
      newValue = value === createPricingPlan?.active;
    }

    if (isPercentageField(name)) {
      const parsedValue = parseFloat(value);
      if (!isNaN(parsedValue) && parsedValue >= 0) {
        newValue = parsedValue > 100 ? 100 : parsedValue;
      } else {
        newValue = "";
      }
    }

    if (isApplicableField(name)) {
      const parsedValue = parseFloat(value);
      if (!isNaN(parsedValue) && parsedValue >= 0) {
        newValue = parsedValue > 1 ? 1 : parsedValue;
      } else {
        newValue = "";
      }
    }

    setPricingPlan((prevState) => {
      const updatedPlan = { ...prevState, [name]: newValue };
      const tempErrors = { ...errors };
      if (
        updatedPlan.minApplicableValue !== "" &&
        updatedPlan.maxApplicableValue !== "" &&
        parseFloat(updatedPlan.minApplicableValue) >
          parseFloat(updatedPlan.maxApplicableValue)
      ) {
        tempErrors.minApplicableValue = createPricingPlan?.errMsg.item1;
        tempErrors.maxApplicableValue = createPricingPlan?.errMsg.item2;
      } else {
        delete tempErrors.minApplicableValue;
        delete tempErrors.maxApplicableValue;
      }

      setErrors(tempErrors);
      return updatedPlan;
    });

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: ""
    }));
  };

  const validate = () => {
    const tempErrors = {};

    if (!pricingPlanName.trim()) {
      tempErrors.pricingPlanName = fieldValidation?.required;
    }

    if (flatRate === null || flatRate === undefined || flatRate === "") {
      tempErrors.flatRate = fieldValidation?.required;
    }

    if (annualRate === null || annualRate === undefined || annualRate === "") {
      tempErrors.annualRate = fieldValidation?.required;
    }

    if (
      extraFlatRate === null ||
      extraFlatRate === undefined ||
      extraFlatRate === ""
    ) {
      tempErrors.extraFlatRate = fieldValidation?.required;
    }

    if (
      extraAnnualRate === null ||
      extraAnnualRate === undefined ||
      extraAnnualRate === ""
    ) {
      tempErrors.extraAnnualRate = fieldValidation?.required;
    }

    if (!triggerEvents || triggerEvents.length === 0) {
      tempErrors.triggerEvents = fieldValidation?.required;
    }

    if (pricingPlan.isRiskBased === true && !primaryPricingPlanId) {
      tempErrors.primaryPricingPlanId = fieldValidation?.required;
    }

    if (pricingPlan.isRiskBased === true && !minApplicableValue) {
      tempErrors.minApplicableValue = fieldValidation?.required;
    }

    if (
      pricingPlan.isRiskBased === true &&
      (minApplicableValue === null ||
        minApplicableValue === undefined ||
        minApplicableValue === "")
    ) {
      tempErrors.minApplicableValue = fieldValidation?.required;
    }

    if (
      pricingPlan.isRiskBased === true &&
      (maxApplicableValue === null ||
        maxApplicableValue === undefined ||
        maxApplicableValue === "")
    ) {
      tempErrors.maxApplicableValue = fieldValidation?.required;
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }
    try {
      const isRiskBased = pricingPlan.isRiskBased;
      const additionalValues = isRiskBased
        ? {
            isDefaultPlan: true,
            planStatus: createPricingPlan?.active,
            pricingPlanType: createPricingPlan?.variable
          }
        : {
            isDefaultPlan: false,
            planStatus: createPricingPlan?.active,
            pricingPlanType: createPricingPlan?.standard
          };
      const dataToSend = {
        ...pricingPlan,
        ...additionalValues,
        primaryPricingPlanId: pricingPlan.primaryPricingPlanId
          ? Number(pricingPlan.primaryPricingPlanId)
          : null,
        invoiceSyncType:
          pricingPlan.invoiceSyncType.trim() !== ""
            ? pricingPlan.invoiceSyncType
            : null,
        eligibleOrganizationType:
          pricingPlan.eligibleOrganizationType.trim() !== ""
            ? pricingPlan.eligibleOrganizationType
            : null,
        applicableCountry:
          pricingPlan.applicableCountry.trim() !== ""
            ? pricingPlan.applicableCountry
            : null,
        ownershipStatus:
          pricingPlan.ownershipStatus.trim() !== ""
            ? pricingPlan.ownershipStatus
            : null,
        triggerEvents: [
          pricingPlan.triggerEvents.map((e) => e.value).join(",")
        ],
        isAutoSwitching: pricingPlan.isAutoSwitching
      };
      const response = await privateAxios.post(getPicingPlanURL, dataToSend);
      showToast(response.data.message, showToastType.success);
      history(PRICING_PLANS);
    } catch (error) {
      const errorMessage =
        error.response?.data?.errors ||
        error.response?.data?.message ||
        commonErrMsg?.item4;

      showToast(errorMessage, showToastType.error);
    }
  };

  const handleCancel = () => {
    setPricingPlan({
      pricingPlanName: "",
      flatRate: "",
      annualRate: "",
      extraFlatRate: "",
      extraAnnualRate: "",
      isAutoSwitching: true,
      triggerEvents: [],
      industryCategoryId: "",
      invoiceSyncType: "",
      eligibleOrganizationType: "",
      applicableCountry: "",
      ownershipStatus: "",
      isRiskBased: false,
      primaryPricingPlanId: "",
      minApplicableValue: "",
      maxApplicableValue: ""
    });
    setErrors({});
    history(PRICING_PLANS);
  };

  const handleSelectChange = (name, selectedOptions) => {
    setPricingPlan((prev) => ({
      ...prev,
      [name]: selectedOptions || []
    }));
  };

  const selectedTriggers = useMemo(
    () =>
      userStatus.filter((opt) => pricingPlan.triggerEvents.includes(opt.value)),
    [userStatus, pricingPlan.triggerEvents]
  );

  const formFields = useMemo(
    () =>
      getPricingPlanFormConfig({
        createPricingPlan,
        createAccPage,
        userStatus,
        selectedTriggers,
        planGroup,
        isRiskBased: pricingPlan.isRiskBased
      }),
    [userStatus, selectedTriggers, planGroup, pricingPlan.isRiskBased]
  );

  return (
    <>
      <TablePagesTitle
        mainTitle={createPricingPlan?.mainTitle}
        content={createPricingPlan?.content}
      />

      <AccessPageAction subheading={createPricingPlan?.content} />

      <div className="row mb-3 mx-0">
        <div className="col-md-12 sale-leade-form">
          <form onSubmit={handleSubmit}>
            {formFields.map((field, index) => (
              <PricingPlanForm
                key={field.name || index}
                field={field}
                formData={pricingPlan}
                errors={errors}
                onChange={onInputChange}
                handleMultiSelect={handleSelectChange}
              />
            ))}

            <div className="row mt-4">
              <div className="col-sm-8 offset-sm-4">
                <button
                  type="button"
                  className="btn btn-red me-2"
                  onClick={handleCancel}
                >
                  {alertActions?.Cancel}
                </button>

                <button type="submit" className="btn btn-primary">
                  {buttonName?.create}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
